import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, FieldArray, ErrorMessage, FastField } from 'formik';
import type { FormikValues } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'flowbite-react';
import {
    DragDropContext, Droppable, Draggable, type DropResult,
    type DraggableProvidedDragHandleProps,
} from 'react-beautiful-dnd';
import {RootState, useAppSelector, useAppDispatch, updateQuote} from '../../../redux';
import { loadProjects } from '../../../redux';
import { Quote, Change, Project, QuoteTextTemplate } from '../../../models';
import type { QuoteItem } from '../../../models';
import {
    quoteItemLineAmount,
    quoteOneTimeTotal,
    quoteMonthlyTotal,
    quoteQuarterlyTotal,
    quoteAnnualTotal,
} from '../../../models/Quote';
import { formatPrice } from '../../../locales';
import { QuoteTextTemplates } from '../../../services';
import {
    SelectField, TypeField, ChangeListItem,
    ChangePicker, Button, ClientSelectField,
    ToggleField, WysiwygField, Wysiwyg, FieldHelp,
    FormActions, DangerZone, Icon
} from '../../../components';
import {
    DEFAULT_QUOTE_CONDITIONS_HTML,
    DEFAULT_QUOTE_INTRO_HTML,
} from '../../../components/QuoteText/QuoteText';
import { newQuoteItemDragId, quoteItemDragId } from './quoteItemOrder';

type TrackedChangeFieldProps = {
    trackedChange?: Change | null;
    projectId?: string;
    onSelect: (change: Change) => void;
    onClear: () => void;
};

/** Compact tracked-change control: selected card or a button that opens a picker modal. */
function TrackedChangeField(props: TrackedChangeFieldProps) {
    const { trackedChange, projectId, onSelect, onClear } = props;
    const { t } = useTranslation();
    const [pickerOpen, setPickerOpen] = useState(false);

    return (
        <div className="col-span-full">
            <label className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                {t('quotes.tracked_change')}
            </label>
            <p className="mt-1 text-center text-xs text-gray-500 dark:text-gray-400">
                {t('quotes.tracked_change_help')}
            </p>
            <div className="mt-2 ml-2 space-y-2">
                {trackedChange?._id ? (
                    <>
                        <ChangeListItem
                            change={trackedChange}
                            showProject
                            onRemove={() => onClear()}
                        />
                        <div className="flex justify-center">
                            <Button
                                title={t('quotes.tracked_change_change')}
                                color="navigate"
                                icon="swap"
                                small
                                onClick={() => setPickerOpen(true)}
                            />
                        </div>
                    </>
                ) : (
                    <div className="flex justify-center">
                        <Button
                            title={t('quotes.tracked_change_choose')}
                            color="navigate"
                            icon="plus"
                            small
                            onClick={() => setPickerOpen(true)}
                        />
                    </div>
                )}
            </div>

            <Modal show={pickerOpen} onClose={() => setPickerOpen(false)} size="2xl">
                <Modal.Header>{t('quotes.tracked_change_choose')}</Modal.Header>
                <Modal.Body>
                    <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                        {t('quotes.tracked_change_help')}
                    </p>
                    <ChangePicker
                        defaultProjectId={projectId}
                        acceptUnestimated
                        acceptFinished
                        listClassName="max-h-[50vh] overflow-auto"
                        onSelect={(change: Change) => {
                            onSelect(change);
                            setPickerOpen(false);
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        title={t('common.cancel')}
                        color="secondary"
                        onClick={() => setPickerOpen(false)}
                    />
                </Modal.Footer>
            </Modal>
        </div>
    );
}

interface QuoteFormProps {
    isCreation: boolean,
    initialValues: FormikValues,
    onSubmit: (values: AnyObj) => void
}

type QuoteItemsSectionProps = {
    items: AnyObj[];
    projectId?: string;
    project: Project;
    updating: string | boolean | undefined;
    estimatedTotal: number;
    setFieldValue: (field: string, value: unknown) => void;
    onSaveDescription: (value: string, index: number) => void;
    onDescriptionDraftChange: (key: string, value: string) => void;
};

type QuoteItemEditorProps = {
    item: AnyObj;
    index: number;
    projectId?: string;
    project: Project;
    updating: string | boolean | undefined;
    linkedChangeIds: Set<string>;
    onSaveDescription: (value: string, index: number) => void;
    setFieldValue: (field: string, value: unknown) => void;
    remove: (index: number) => void;
    replace: (index: number, value: AnyObj) => void;
    onDescriptionDraftChange: (key: string, value: string) => void;
    dragRef: (element: HTMLElement | null) => void;
    dragProps: React.HTMLAttributes<HTMLElement>;
    dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
    isDragging: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onMoveUp: () => void;
    onMoveDown: () => void;
};

const quoteItemDraftKey = (item: AnyObj, index: number) => String(item._id || item.__dragId || `idx-${index}`);

function QuoteItemsSection(props: QuoteItemsSectionProps) {
    const {
        items, projectId, project, updating, estimatedTotal,
        setFieldValue, onSaveDescription, onDescriptionDraftChange,
    } = props;
    const { t } = useTranslation();

    const linkedIdsKey = (items || [])
        .map((entry: QuoteItem) => entry.change?._id || '')
        .join(',');
    const linkedChangeIds = useMemo(
        () => new Set(
            (items || [])
                .map((entry: QuoteItem) => entry.change?._id)
                .filter(Boolean) as string[]
        ),
        [linkedIdsKey]
    );

    const oneTimeTotal = quoteOneTimeTotal(items);
    const monthlyTotal = quoteMonthlyTotal(items);
    const quarterlyTotal = quoteQuarterlyTotal(items);
    const annualTotal = quoteAnnualTotal(items);

    return (
        <div className="mt-5">
            <h4 className="text-center text-xl font-bold leading-7 text-gray-900 dark:text-gray-100">
                {t('quotes.total_price', {total: formatPrice(oneTimeTotal)})}
            </h4>
            {monthlyTotal > 0 && (
                <h4 className="text-center text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100">
                    {t('quotes.total_monthly_price', {total: formatPrice(monthlyTotal)})}
                </h4>
            )}
            {quarterlyTotal > 0 && (
                <h4 className="text-center text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100">
                    {t('quotes.total_quarterly_price', {total: formatPrice(quarterlyTotal)})}
                </h4>
            )}
            {annualTotal > 0 && (
                <h4 className="text-center text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100">
                    {t('quotes.total_annual_price', {total: formatPrice(annualTotal)})}
                </h4>
            )}
            <h4 className="mb-4 text-center font-light leading-7 text-gray-900 dark:text-gray-100">
                {t('quotes.total_estimated', {total: estimatedTotal.toFixed(2)})}
            </h4>
            <hr />
            <FieldArray name="items">
                {({ remove, push, replace, move }) => {
                    const onDragEnd = (result: DropResult) => {
                        if (!result.destination)
                            return;
                        if (result.destination.index === result.source.index)
                            return;
                        move(result.source.index, result.destination.index);
                    };

                    return (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="quote-items">
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="flex flex-col gap-4 mt-4"
                                    >
                                        {items.map((item: AnyObj, i: number) => {
                                            const dragId = quoteItemDragId(item, i);
                                            return (
                                                <Draggable key={dragId} draggableId={dragId} index={i}>
                                                    {(dragProvided, snapshot) => (
                                                        <QuoteItemEditor
                                                            item={item}
                                                            index={i}
                                                            projectId={projectId}
                                                            project={project}
                                                            updating={updating}
                                                            linkedChangeIds={linkedChangeIds}
                                                            onSaveDescription={onSaveDescription}
                                                            setFieldValue={setFieldValue}
                                                            remove={remove}
                                                            replace={replace}
                                                            onDescriptionDraftChange={onDescriptionDraftChange}
                                                            dragRef={dragProvided.innerRef}
                                                            dragProps={dragProvided.draggableProps}
                                                            dragHandleProps={dragProvided.dragHandleProps}
                                                            isDragging={snapshot.isDragging}
                                                            canMoveUp={i > 0}
                                                            canMoveDown={i < items.length - 1}
                                                            onMoveUp={() => move(i, i - 1)}
                                                            onMoveDown={() => move(i, i + 1)}
                                                        />
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                            <div className="flex justify-center mt-4">
                                <Button
                                    onClick={() => push({
                                        title: '',
                                        type: Change.TYPE_FEATURE,
                                        price: 0,
                                        estimate: 0,
                                        billingPeriod: 'oneTime',
                                        removable: false,
                                        quantityMode: false,
                                        quantity: 1,
                                        quantityMin: 1,
                                        quantityMax: 1,
                                        free: false,
                                        __dragId: newQuoteItemDragId(),
                                    })}
                                    title={t('quotes.add_item')}
                                    color="navigate"
                                    icon="plus"
                                    small
                                />
                            </div>
                        </DragDropContext>
                    );
                }}
            </FieldArray>
        </div>
    );
}

/** Isolated row: avoids re-rendering ChangePicker lists on every keystroke in sibling rows. */
const QuoteItemEditor = React.memo(function QuoteItemEditor(props: QuoteItemEditorProps) {
    const {
        item, index, projectId, project, updating, linkedChangeIds,
        onSaveDescription, setFieldValue, remove, replace, onDescriptionDraftChange,
        dragRef, dragProps, dragHandleProps, isDragging,
        canMoveUp, canMoveDown, onMoveUp, onMoveDown,
    } = props;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [showChangePicker, setShowChangePicker] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState(item.description || '');
    const draftKey = quoteItemDraftKey(item, index);
    // WysiwygField calls onDraftChange('') after onSave via _onClose — keep the saved HTML.
    const pendingSavedDescriptionRef = useRef<string | null>(null);

    useEffect(() => {
        setDescriptionDraft(item.description || '');
        onDescriptionDraftChange(draftKey, item.description || '');
    }, [item._id, item.__dragId, item.description, draftKey, onDescriptionDraftChange]);

    const updateDescriptionDraft = useCallback((value: string) => {
        setDescriptionDraft(value);
        onDescriptionDraftChange(draftKey, value);
    }, [draftKey, onDescriptionDraftChange]);

    const handleDescriptionDraftChange = useCallback((value: string) => {
        if (pendingSavedDescriptionRef.current !== null) {
            const saved = pendingSavedDescriptionRef.current;
            pendingSavedDescriptionRef.current = null;
            updateDescriptionDraft(saved);
            return;
        }
        // Cancel path: WysiwygField clears the draft with ''.
        if (value === '') {
            updateDescriptionDraft(item.description || '');
            return;
        }
        updateDescriptionDraft(value);
    }, [item.description, updateDescriptionDraft]);

    const filterChanges = useCallback((changes: Change[]) => {
        return (changes || []).filter((change: Change) => {
            if (!change.isQuoteable())
                return false;
            if (item.change?._id && change._id === item.change._id)
                return true;
            return !linkedChangeIds.has(change._id);
        });
    }, [item.change?._id, linkedChangeIds]);

    return (
        <div
            ref={dragRef}
            {...dragProps}
            className={[
                'border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700',
                isDragging ? 'shadow-lg relative z-10' : '',
            ].filter(Boolean).join(' ')}
        >
            <div className="flex items-center justify-between gap-2 px-3 pt-3">
                <div
                    {...(dragHandleProps || {})}
                    className="cursor-grab text-gray-400 hover:text-gray-600 dark:text-gray-300 px-1 py-1"
                    title={t('common.drag_to_reorder') || 'Drag to reorder'}
                >
                    <Icon type="dots" size={4} />
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={onMoveUp}
                        disabled={!canMoveUp}
                        title={t('quotes.move_item_up') || 'Move up'}
                        className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none"
                    >
                        <Icon type="up" size={4} />
                    </button>
                    <button
                        type="button"
                        onClick={onMoveDown}
                        disabled={!canMoveDown}
                        title={t('quotes.move_item_down') || 'Move down'}
                        className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none"
                    >
                        <Icon type="down" size={4} />
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-x-2 gap-y-2 p-3 pt-2">
                <div className="col-span-1">
                    <FastField
                        type="text"
                        name={`items[${index}].title`}
                        placeholder={t('quotes.title_placeholder')}
                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 py-1 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                        required
                    />
                </div>
                <div className="col-span-1">
                    <WysiwygField
                        rows={6}
                        name={`items[${index}].description`}
                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 py-1 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                        inline
                        editOnClick
                        placeholder={t('quotes.description_placeholder') || ''}
                        draftValue={descriptionDraft}
                        onDraftChange={handleDescriptionDraftChange}
                        onSave={(value: string) => {
                            pendingSavedDescriptionRef.current = value;
                            updateDescriptionDraft(value);
                            setFieldValue(`items[${index}].description`, value);
                            onSaveDescription(value, index);
                        }}
                        isLoading={updating === 'pending'}
                    />
                </div>
                <div className="col-span-1 p-2 bg-gray-100 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600">
                    <TypeField name={`items[${index}].type`} />
                </div>
                <div className="col-span-1 flex flex-col gap-2 py-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="flex flex-row items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                            <FastField
                                type="number"
                                step={0.5}
                                name={`items[${index}].estimate`}
                                className="block w-full border-0 rounded-none py-1 font-bold text-center text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                            />
                            <div className="w-12 shrink-0 text-center bg-gray-300 dark:bg-gray-600 px-1 py-1 text-gray-900 dark:text-gray-100 font-bold sm:text-sm sm:leading-6">h</div>
                        </div>
                        <div className="flex flex-row items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                            <FastField
                                type="number"
                                step={0.01}
                                name={`items[${index}].price`}
                                className="block w-full border-0 rounded-none font-bold text-center py-1 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                                required
                            />
                            <div className="min-w-14 shrink-0 text-center bg-gray-300 dark:bg-gray-600 px-1 py-1 text-gray-600 dark:text-gray-100 font-bold sm:text-sm sm:leading-6 inline-flex items-center justify-center gap-1">
                                CHF
                                <FieldHelp content={item.quantityMode
                                    ? (t('quotes.help.item_unit_price') || '')
                                    : (t('quotes.help.item_estimate_price') || '')} />
                            </div>
                        </div>
                        <div className="flex flex-row overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                            <div className="flex justify-center items-center px-2">
                                <ToggleField name={`items[${index}].free`} />
                            </div>
                            <div className="flex justify-center items-center px-2 font-bold text-gray-600 dark:text-gray-100 text-sm bg-gray-300 dark:bg-gray-600 gap-1 flex-1">
                                {t('quotes.free_item')}
                                <FieldHelp content={t('quotes.help.free_item') || ''} />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-row items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                        <div className="px-2 py-1 text-sm font-bold bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 whitespace-nowrap inline-flex items-center gap-1">
                            {t('quotes.billing_period')}
                            <FieldHelp content={t('quotes.help.billing_period') || ''} />
                        </div>
                        <Field
                            as="select"
                            name={`items[${index}].billingPeriod`}
                            className="flex-1 border-0 py-1 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
                        >
                            <option value="oneTime">{t('quotes.billing_period_one_time')}</option>
                            <option value="monthly">{t('quotes.billing_period_monthly')}</option>
                            <option value="quarterly">{t('quotes.billing_period_quarterly')}</option>
                            <option value="annual">{t('quotes.billing_period_annual')}</option>
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className={`flex flex-row overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 ${item.quantityMode ? 'opacity-50 pointer-events-none' : ''}`}>
                            <div className="flex justify-center items-center px-2">
                                <ToggleField name={`items[${index}].removable`} />
                            </div>
                            <div className="flex justify-center items-center px-2 font-bold text-gray-600 dark:text-gray-100 text-sm bg-gray-300 dark:bg-gray-600 gap-1 flex-1">
                                {t('quotes.removable_item')}
                                <FieldHelp content={t('quotes.help.removable_item') || ''} />
                            </div>
                        </div>
                        <div className="flex flex-row overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                            <div className="flex justify-center items-center px-2">
                                <ToggleField
                                    name={`items[${index}].quantityMode`}
                                    onChange={(checked: boolean) => {
                                        setFieldValue(`items[${index}].quantityMode`, checked);
                                        if (checked) {
                                            setFieldValue(`items[${index}].removable`, false);
                                            if (item.quantity == null)
                                                setFieldValue(`items[${index}].quantity`, 1);
                                            if (item.quantityMin == null)
                                                setFieldValue(`items[${index}].quantityMin`, 1);
                                            if (item.quantityMax == null)
                                                setFieldValue(`items[${index}].quantityMax`, 1);
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex justify-center items-center px-2 font-bold text-gray-600 dark:text-gray-100 text-sm bg-gray-300 dark:bg-gray-600 gap-1 flex-1">
                                {t('quotes.quantity_mode')}
                                <FieldHelp content={t('quotes.help.quantity_mode') || ''} />
                            </div>
                        </div>
                    </div>

                    {item.quantityMode && (
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                            <div className="flex flex-row items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                                <FastField
                                    type="number"
                                    min={0}
                                    step={1}
                                    name={`items[${index}].quantity`}
                                    className="block w-full border-0 rounded-none py-1 font-bold text-center text-gray-900 dark:text-gray-100 sm:text-sm sm:leading-6"
                                />
                                <div className="w-20 shrink-0 text-center bg-gray-300 dark:bg-gray-600 px-1 py-1 text-gray-900 dark:text-gray-100 font-bold sm:text-sm sm:leading-6">
                                    {t('quotes.quantity')}
                                </div>
                            </div>
                            <div className="flex flex-row items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                                <FastField
                                    type="number"
                                    min={0}
                                    step={1}
                                    name={`items[${index}].quantityMin`}
                                    className="block w-full border-0 rounded-none font-bold text-center py-1 text-gray-900 dark:text-gray-100 sm:text-sm sm:leading-6"
                                />
                                <div className="w-14 shrink-0 text-center bg-gray-300 dark:bg-gray-600 px-1 py-1 text-gray-900 dark:text-gray-100 font-bold sm:text-sm sm:leading-6">
                                    {t('quotes.quantity_min')}
                                </div>
                            </div>
                            <div className="flex flex-row items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                                <FastField
                                    type="number"
                                    min={0}
                                    step={1}
                                    name={`items[${index}].quantityMax`}
                                    className="block w-full border-0 rounded-none font-bold text-center py-1 text-gray-900 dark:text-gray-100 sm:text-sm sm:leading-6"
                                />
                                <div className="w-14 shrink-0 text-center bg-gray-300 dark:bg-gray-600 px-1 py-1 text-gray-900 dark:text-gray-100 font-bold sm:text-sm sm:leading-6">
                                    {t('quotes.quantity_max')}
                                </div>
                            </div>
                            <div className="flex items-center justify-center px-2 text-sm font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600">
                                {t('quotes.line_total')}: {formatPrice(quoteItemLineAmount(item))}
                            </div>
                        </div>
                    )}
                </div>
                {item.change ? (
                    <>
                        <div className="font-bold text-sm inline-flex items-center gap-1">
                            {t('quotes.this_item_is_linked_to_this_change')}
                            <FieldHelp content={t('quotes.help.link_change') || ''} />
                        </div>
                        <div onClick={() => navigate(Project.path(project, 'change', item.change._id))}>
                            <ChangeListItem
                                change={item.change}
                                onRemove={(change: Change, e: AnyObj) => {
                                    e.stopPropagation();
                                    replace(index, { ...item, change: undefined });
                                    setShowChangePicker(false);
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="font-bold text-sm inline-flex items-center gap-1">
                            {t('quotes.choose_a_change')}
                            <FieldHelp content={t('quotes.help.link_change') || ''} />
                        </div>
                        {!showChangePicker ? (
                            <Button
                                onClick={() => setShowChangePicker(true)}
                                title={t('quotes.choose_a_change')}
                                color="navigate"
                                icon="plus"
                                small
                            />
                        ) : (
                            <ChangePicker
                                defaultProjectId={projectId}
                                onSelect={(change: Change) => {
                                    replace(index, {
                                        ...item,
                                        type: change.type,
                                        title: change.localizedTitle(i18n.language),
                                        description: change.localizedDescription(i18n.language),
                                        change,
                                        estimate: change.estimate,
                                    });
                                    updateDescriptionDraft(change.localizedDescription(i18n.language) || '');
                                    setShowChangePicker(false);
                                }}
                                className="block w-full"
                                listClassName="max-h-[400px] overflow-auto"
                                selectClassName="max-h-[400px] overflow-auto"
                                filterChanges={filterChanges}
                            />
                        )}
                    </>
                )}
            </div>
            <div className="cursor-pointer flex justify-center items-center mr-2 mb-2">
                <Button
                    onClick={() => remove(index)}
                    title={t('quotes.remove_this_item')}
                    color="danger"
                    icon="delete"
                    small
                />
            </div>
        </div>
    );
});

/** Resolve intro/conditions HTML for a new quote once templates have loaded. */
function resolveQuoteTextDefaults(
    kind: 'intro' | 'conditions',
    templates: QuoteTextTemplate[],
    currentValue: string | undefined,
): string {
    if (String(currentValue || '').trim())
        return currentValue || '';
    const def = templates.find((tpl) => tpl.isDefault) || templates[0];
    if (kind === 'intro')
        return def?.body || DEFAULT_QUOTE_INTRO_HTML;
    return def?.body || DEFAULT_QUOTE_CONDITIONS_HTML;
}

function QuoteForm(props: QuoteFormProps) {
    const { isCreation, initialValues, onSubmit } = props;

    const { t } = useTranslation();
    const dispatch    = useAppDispatch();

    const { projects } = useAppSelector((state : RootState) => state.projects);
    const { updating } = useAppSelector((state: RootState) => state.quotes);

    const [introTemplates, setIntroTemplates] = useState<QuoteTextTemplate[]>([]);
    const [conditionsTemplates, setConditionsTemplates] = useState<QuoteTextTemplate[]>([]);
    const [templatesReady, setTemplatesReady] = useState(false);

    useEffect(()=>{
        dispatch(loadProjects());
    },[dispatch]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [introJson, conditionsJson] = await Promise.all([
                    QuoteTextTemplates.list({ kind: 'intro' }),
                    QuoteTextTemplates.list({ kind: 'conditions' }),
                ]);
                if (cancelled)
                    return;
                setIntroTemplates(introJson.quote_text_templates || []);
                setConditionsTemplates(conditionsJson.quote_text_templates || []);
            } catch {
                // Form still works with hardcoded fallbacks
            } finally {
                if (!cancelled)
                    setTemplatesReady(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const quote = new Quote(initialValues);

    const applyTemplateBody = useCallback((
        field: 'introBody' | 'conditionsBody',
        template: QuoteTextTemplate,
        currentValue: string,
        setFieldValue: (field: string, value: unknown) => void,
    ) => {
        const next = template.body || '';
        if (String(currentValue || '').trim() && String(currentValue).trim() !== next.trim()) {
            if (!window.confirm(String(t('quote_text_templates.apply_confirm') || '')))
                return;
        }
        setFieldValue(field, next);
    }, [t]);

    const validate = (values: AnyObj) => {
        const invalid: AnyObj = {};

        if (!values.name)
            invalid.name = t('common.validation.this_field_is_required');

        if (!values.project)
            invalid.project = t('common.validation.this_field_is_required');

        return invalid;
    }

    const projectOptions = React.useMemo(() => {
        return projects?.map((project: Project) => ({
            value: project._id,
            label: project.name
        }));
    }, [projects]);

    const descriptionDraftsRef = useRef<Record<string, string>>({});
    const onDescriptionDraftChange = useCallback((key: string, value: string) => {
        descriptionDraftsRef.current[key] = value;
    }, []);

    const onSaveQuoteItemDescription = useCallback((value: string, index: number) => {
        const items = (quote.items || []).map((entry: QuoteItem, i: number) => (
            i === index ? { ...entry, description: value } : entry
        ));
        return dispatch(updateQuote({ _id: quote._id, items }, true));
    }, [dispatch, quote._id, quote.items]);

    const mergeDescriptionDrafts = useCallback((values: AnyObj) => {
        const drafts = descriptionDraftsRef.current;
        const items = (values.items || []).map((item: AnyObj, index: number) => {
            const key = quoteItemDraftKey(item, index);
            const { __dragId: _dragId, ...rest } = item;
            void _dragId;
            if (!Object.prototype.hasOwnProperty.call(drafts, key))
                return rest;
            return { ...rest, description: drafts[key] };
        });
        return { ...values, items };
    }, []);

    // Seed intro/conditions into Formik initialValues (create only) so Wysiwyg
    // never receives a mid-typing setFieldValue that recreates EditorState.
    const formInitialValues: FormikValues = useMemo(() => {
        const linkedChanges = (initialValues.items || [])
            .map((item: QuoteItem) => item.change)
            .filter((change: Change | undefined) => !!change?._id) as Change[];
        const uniqueLinked = linkedChanges.filter(
            (change, index, all) =>
                all.findIndex((entry) => entry._id === change._id) === index
        );
        const suggestedTracked = initialValues.trackedChange?._id
            ? initialValues.trackedChange
            : (uniqueLinked.length === 1 ? uniqueLinked[0] : null);

        if (!isCreation) {
            return {
                ...initialValues,
                trackedChange: suggestedTracked,
                introBody: initialValues.introBody || '',
                conditionsBody: initialValues.conditionsBody || '',
            };
        }
        return {
            ...initialValues,
            trackedChange: initialValues.trackedChange || null,
            introBody: resolveQuoteTextDefaults('intro', introTemplates, initialValues.introBody),
            conditionsBody: resolveQuoteTextDefaults(
                'conditions',
                conditionsTemplates,
                initialValues.conditionsBody,
            ),
        };
    }, [isCreation, initialValues, introTemplates, conditionsTemplates]);

    if (isCreation && !templatesReady) {
        return (
            <div className="QuoteForm bg-white dark:bg-gray-800 border-b px-4 py-6 sm:px-6 lg:px-8">
                <main className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('common.loading') || '…'}
                </main>
            </div>
        );
    }

    return (
        <div className="QuoteForm bg-white dark:bg-gray-800 border-b px-4 py-6 sm:px-6 lg:px-8">
            <main>
                <Formik
                    initialValues={formInitialValues}
                    enableReinitialize
                    validate={validate}
                    onSubmit={(values) => {
                        onSubmit(mergeDescriptionDrafts(values));
                    }}
                >
                    {({ values, setFieldValue }) => (
                        <Form>
                            <div className="space-y-6">
                                <div className="flex flex-col space-y-4 justify-center">
                                    <div className="grid grid-cols-1 gap-y-2 md:grid-cols-12">
                                        <div className="col-span-10 lg:col-span-5">
                                            <label htmlFor="name" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                {t('quotes.name')}
                                            </label>
                                            <div className="mt-2">
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    className={`h-[42px] text-center block w-full rounded-md ${!isCreation ? 'md:rounded-r-none' : ''} border-1 border-gray-300 dark:border-gray-600 py-1.5 text-gray-900 dark:text-gray-100 font-bold placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                                                    required
                                                />
                                                <ErrorMessage name="name" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                            </div>
                                        </div>
                                        {!isCreation && (
                                            <div className="col-span-2 lg:col-span-1">
                                                <label htmlFor="slug" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                    {t('quotes.slug')}
                                                </label>
                                                <div className="mt-2">
                                                    <Field
                                                        type="text"
                                                        name="slug"
                                                        disabled
                                                        className="h-[42px] block w-full rounded-md bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 border-1 md:border-l-0 md:rounded-l-none md:rounded-r-none py-1.5 text-gray-900 dark:text-gray-100 font-bold placeholder:text-gray-400 sm:text-sm sm:leading-6 text-center"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="col-span-full lg:col-span-3">
                                            <label htmlFor="project" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                {t('quotes.project')}
                                            </label>
                                            {isCreation ? (
                                                <div className="mt-2 ml-2 border rounded-md">
                                                    <SelectField
                                                        name="project._id"
                                                        placeholder={t('changes.choose_a_project') || ''}
                                                        options={projectOptions}
                                                    />
                                                    <ErrorMessage name="project" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                                </div>
                                            ) : (
                                                <div className="mt-2">
                                                    <Field
                                                        type="text"
                                                        name="project.name"
                                                        disabled
                                                        className="h-[42px] block w-full rounded-md bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 border-1 md:border-l-0 md:rounded-l-none py-1.5 text-gray-900 dark:text-gray-100 font-bold placeholder:text-gray-400 sm:text-sm sm:leading-6 text-center"
                                                        required
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-span-full lg:col-span-3">
                                            <label htmlFor="client" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                {t('quotes.client')}
                                            </label>
                                            <div className="mt-2 ml-2">
                                                <ClientSelectField
                                                    name="client"
                                                    placeholder={t('changes.choose_a_client') || ''}
                                                />
                                            </div>
                                        </div>
                                        <TrackedChangeField
                                            trackedChange={values.trackedChange}
                                            projectId={values?.project?._id}
                                            onClear={() => setFieldValue('trackedChange', null)}
                                            onSelect={(change: Change) => {
                                                setFieldValue('trackedChange', change);
                                                if (isCreation) {
                                                    if (change.project)
                                                        setFieldValue('project', change.project);
                                                    if (change.client)
                                                        setFieldValue('client', change.client);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <hr />
                                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {t('quote_text_templates.intro_section')}
                                            </h4>
                                            {introTemplates.length > 0 && (
                                                <select
                                                    className="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                                    defaultValue=""
                                                    onChange={(e) => {
                                                        const tpl = introTemplates.find((item) => item._id === e.target.value);
                                                        e.target.value = '';
                                                        if (tpl)
                                                            applyTemplateBody('introBody', tpl, values.introBody || '', setFieldValue);
                                                    }}
                                                >
                                                    <option value="" disabled>
                                                        {t('quote_text_templates.apply')}
                                                    </option>
                                                    {introTemplates.map((tpl) => (
                                                        <option key={tpl._id} value={tpl._id}>
                                                            {tpl.name}{tpl.isDefault ? ` (${t('quote_text_templates.default_badge')})` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        <Wysiwyg
                                            value={values.introBody || ''}
                                            onChange={(value: string) => setFieldValue('introBody', value)}
                                            rows={8}
                                            minimal
                                            className="text-gray-900 dark:text-gray-100 sm:text-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {t('quote_text_templates.conditions_section')}
                                            </h4>
                                            {conditionsTemplates.length > 0 && (
                                                <select
                                                    className="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                                                    defaultValue=""
                                                    onChange={(e) => {
                                                        const tpl = conditionsTemplates.find((item) => item._id === e.target.value);
                                                        e.target.value = '';
                                                        if (tpl)
                                                            applyTemplateBody('conditionsBody', tpl, values.conditionsBody || '', setFieldValue);
                                                    }}
                                                >
                                                    <option value="" disabled>
                                                        {t('quote_text_templates.apply')}
                                                    </option>
                                                    {conditionsTemplates.map((tpl) => (
                                                        <option key={tpl._id} value={tpl._id}>
                                                            {tpl.name}{tpl.isDefault ? ` (${t('quote_text_templates.default_badge')})` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        <Wysiwyg
                                            value={values.conditionsBody || ''}
                                            onChange={(value: string) => setFieldValue('conditionsBody', value)}
                                            rows={8}
                                            minimal
                                            className="text-gray-900 dark:text-gray-100 sm:text-sm"
                                        />
                                    </div>
                                </div>
                                <hr />
                                {!isCreation && (
                                    <QuoteItemsSection
                                        items={values.items || []}
                                        projectId={values?.project?._id}
                                        project={quote.project}
                                        updating={updating}
                                        estimatedTotal={quote.totalEstimated()}
                                        setFieldValue={setFieldValue}
                                        onSaveDescription={onSaveQuoteItemDescription}
                                        onDescriptionDraftChange={onDescriptionDraftChange}
                                    />
                                )}
                            </div>

                            <FormActions
                                cancelTo={isCreation ? '/quotes' : `/quotes/${quote?._id}`}
                                cancelTitle={isCreation ? undefined : String(t('common.back') || '')}
                                loading={updating === 'pending'}
                            />
                        </Form>
                    )}
                </Formik>
                {!isCreation && quote?._id && (
                    <DangerZone deleteTo={`/quotes/delete/${quote._id}`} />
                )}
            </main>
        </div>
    );
}

export default QuoteForm;
